import React from 'react';
import {
 RefreshCw,
 Loader2,
 ListChecks,
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import { useAdminPerformance } from '../../hooks/useAdminPerformance';
import { useAdminInfrastructure } from '../../hooks/useAdminInfrastructure';
import InfrastructurePanel from '../../components/admin/InfrastructurePanel';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminPerformance = () => {
 const {
 performance,
 isLoading,
 error,
 period,
 setPeriod,
 periodOptions,
 refetch
 } = useAdminPerformance('month');

 const {
 infrastructure,
 isLoading: infraLoading,
 refetch: refetchInfra,
 } = useAdminInfrastructure();

 const getNumeric = (val) => {
 if (val === null || val === undefined || val === '') return 0;
 const strVal = String(val).trim();
 if (strVal === '' || strVal === 'null' || strVal === 'undefined' || strVal === 'NaN') {
 return 0;
 }
 const cleaned = strVal.replace(/[^0-9.-]/g, '');
 if (cleaned === '' || cleaned === '-' || cleaned === '.') {
 return 0;
 }
 const parsed = parseFloat(cleaned);
 if (isNaN(parsed) || !isFinite(parsed)) {
 return 0;
 }
 return parsed;
 };

 const getStatusBadge = (status) => {
 return (
 <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${ status === 'healthy' || status === 'operational' || status === 'good' || status === 'normal' ? 'bg-emerald-100 text-emerald-800' : status === 'warning' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800' }`}>
 {status}
 </span>
 );
 };

 if (error) {
 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Performance"
 subtitle="Monitor platform performance and system health"
 />
 <div className="rounded-lg border border-red-200 bg-surface p-6 text-center shadow-sm">
 <p className="text-red-600">Failed to load performance data. Please try again.</p>
 </div>
 </div>
 );
 }

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Performance"
 subtitle="Monitor platform performance and system health"
 actions={
 <>
 <SearchSelect
 value={period}
 onChange={setPeriod}
 options={periodOptions}
 className="w-40"
 />
 <button
 type="button"
 onClick={() => refetch()}
 className="p-2.5 border border-border rounded-md bg-surface text-ink hover:bg-surface-muted"
 >
 <RefreshCw className="w-4 h-4" />
 </button>
 </>
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.performance.title}
 steps={ADMIN_PAGE_GUIDES.performance.steps}
 icon={ListChecks}
 />

 {isLoading ? (
 <div className="rounded-lg border border-border bg-surface shadow-sm py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading performance data…</p>
 </div>
 ) : (
 <>
 <InfrastructurePanel
 infrastructure={infrastructure}
 isLoading={infraLoading}
 onRefresh={refetchInfra}
 />

 {performance.system_metrics && (
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-5">
 <h2 className="text-lg font-semibold text-ink mb-4">System Metrics</h2>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 {performance.system_metrics.uptime && (
 <div className="p-4 rounded-xl bg-surface-muted/60">
 <div className="flex items-center justify-between mb-2">
 <span className="text-sm font-semibold text-ink-secondary">Uptime</span>
 {getStatusBadge(performance.system_metrics.uptime.status)}
 </div>
 <p className="text-2xl font-bold text-ink tabular-nums">
 {performance.system_metrics.uptime.percentage}%
 </p>
 </div>
 )}

 {performance.system_metrics.response_time && (
 <div className="p-4 rounded-xl bg-surface-muted/60">
 <div className="flex items-center justify-between mb-2">
 <span className="text-sm font-semibold text-ink-secondary">Response Time</span>
 {getStatusBadge(performance.system_metrics.response_time.status)}
 </div>
 <p className="text-2xl font-bold text-ink tabular-nums">
 {performance.system_metrics.response_time.average}ms
 </p>
 <p className="text-xs text-gray-500 mt-1">
 P95: {performance.system_metrics.response_time.p95}ms
 </p>
 </div>
 )}

 {performance.system_metrics.server_resources && (
 <div className="p-4 rounded-xl bg-surface-muted/60">
 <div className="flex items-center justify-between mb-2">
 <span className="text-sm font-semibold text-ink-secondary">Server Resources</span>
 </div>
 <div className="space-y-2">
 <div className="flex justify-between">
 <span className="text-xs text-ink-secondary">CPU</span>
 <span className="text-xs font-medium tabular-nums text-ink">
 {performance.system_metrics.server_resources.cpu?.usage}%
 </span>
 </div>
 <div className="flex justify-between">
 <span className="text-xs text-ink-secondary">Memory</span>
 <span className="text-xs font-medium tabular-nums text-ink">
 {performance.system_metrics.server_resources.memory?.usage}%
 </span>
 </div>
 <div className="flex justify-between">
 <span className="text-xs text-ink-secondary">Disk</span>
 <span className="text-xs font-medium tabular-nums text-ink">
 {performance.system_metrics.server_resources.disk?.usage}%
 </span>
 </div>
 </div>
 </div>
 )}

 {performance.system_metrics.database && (
 <div className="p-4 rounded-xl bg-surface-muted/60">
 <div className="flex items-center justify-between mb-2">
 <span className="text-sm font-semibold text-ink-secondary">Database</span>
 {getStatusBadge(performance.system_metrics.database.status)}
 </div>
 <p className="text-sm text-ink-secondary">
 Connections: {performance.system_metrics.database.connections}
 </p>
 <p className="text-sm text-ink-secondary">
 Query Time: {performance.system_metrics.database.query_time}ms
 </p>
 </div>
 )}
 </div>
 </div>
 )}

 {performance.application_metrics && (
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-5">
 <h2 className="text-lg font-semibold text-ink mb-4">Payment Processing</h2>
 {performance.application_metrics.payment_processing && (
 <div className="space-y-3">
 <div className="flex justify-between">
 <span className="text-sm text-ink-secondary">Total Payments</span>
 <span className="text-sm font-medium tabular-nums text-ink">
 {performance.application_metrics.payment_processing.total_payments || 0}
 </span>
 </div>
 <div className="flex justify-between">
 <span className="text-sm text-ink-secondary">Successful</span>
 <span className="text-sm font-medium text-emerald-600 tabular-nums">
 {performance.application_metrics.payment_processing.successful || 0}
 </span>
 </div>
 <div className="flex justify-between">
 <span className="text-sm text-ink-secondary">Failed</span>
 <span className="text-sm font-medium text-red-600 tabular-nums">
 {performance.application_metrics.payment_processing.failed || 0}
 </span>
 </div>
 <div className="flex justify-between">
 <span className="text-sm text-ink-secondary">Success Rate</span>
 <span className="text-sm font-bold tabular-nums text-ink">
 {getNumeric(performance.application_metrics.payment_processing.success_rate).toFixed(2)}%
 </span>
 </div>
 </div>
 )}
 </div>

 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-5">
 <h2 className="text-lg font-semibold text-ink mb-4">Booking Processing</h2>
 {performance.application_metrics.booking_processing && (
 <div className="space-y-3">
 <div className="flex justify-between">
 <span className="text-sm text-ink-secondary">Total Bookings</span>
 <span className="text-sm font-medium tabular-nums text-ink">
 {performance.application_metrics.booking_processing.total_bookings || 0}
 </span>
 </div>
 <div className="flex justify-between">
 <span className="text-sm text-ink-secondary">Completed</span>
 <span className="text-sm font-medium text-emerald-600 tabular-nums">
 {performance.application_metrics.booking_processing.completed || 0}
 </span>
 </div>
 <div className="flex justify-between">
 <span className="text-sm text-ink-secondary">Cancelled</span>
 <span className="text-sm font-medium text-red-600 tabular-nums">
 {performance.application_metrics.booking_processing.cancelled || 0}
 </span>
 </div>
 <div className="flex justify-between">
 <span className="text-sm text-ink-secondary">Completion Rate</span>
 <span className="text-sm font-bold tabular-nums text-ink">
 {getNumeric(performance.application_metrics.booking_processing.completion_rate).toFixed(2)}%
 </span>
 </div>
 </div>
 )}
 </div>
 </div>
 )}

 {performance.business_metrics && (
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-5">
 <h2 className="text-lg font-semibold text-ink mb-4">Business Metrics</h2>
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
 <div className="p-3 rounded-xl bg-surface-muted/60">
 <p className="text-xs text-ink-secondary">Conversion Rate</p>
 <p className="text-lg font-bold text-ink tabular-nums">
 {getNumeric(performance.business_metrics.conversion_rate).toFixed(2)}%
 </p>
 </div>
 <div className="p-3 rounded-xl bg-surface-muted/60">
 <p className="text-xs text-ink-secondary">Avg Booking Value</p>
 <p className="text-lg font-bold text-ink tabular-nums">
 ${getNumeric(performance.business_metrics.average_booking_value).toFixed(2)}
 </p>
 </div>
 {performance.business_metrics.customer_satisfaction && (
 <div className="p-3 rounded-xl bg-surface-muted/60">
 <p className="text-xs text-ink-secondary">Avg Rating</p>
 <p className="text-lg font-bold text-ink tabular-nums">
 {getNumeric(performance.business_metrics.customer_satisfaction.average_rating).toFixed(1)}
 </p>
 <p className="text-xs text-gray-500">
 {performance.business_metrics.customer_satisfaction.total_reviews || 0} reviews
 </p>
 </div>
 )}
 {performance.business_metrics.vendor_performance && (
 <div className="p-3 rounded-xl bg-surface-muted/60">
 <p className="text-xs text-ink-secondary">Active Vendors</p>
 <p className="text-lg font-bold text-ink tabular-nums">
 {performance.business_metrics.vendor_performance.active_vendors || 0}
 </p>
 </div>
 )}
 </div>
 </div>
 )}

 {performance.vendor_leaderboard?.length > 0 && (
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-5">
 <h2 className="text-lg font-semibold text-ink mb-2">Vendor performance leaderboard</h2>
 <p className="text-sm text-ink-secondary mb-4">
 Composite score from completion rate, ratings, booking volume, and cancellations (last 90 days).
 </p>
 <div className="space-y-2">
 {performance.vendor_leaderboard.map((vendor, index) => (
 <div
 key={vendor.id}
 className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-xl bg-surface-muted/60"
 >
 <div className="flex items-center gap-3 min-w-0">
 <span className="text-sm font-bold text-gray-400 w-6">#{index + 1}</span>
 <div className="min-w-0">
 <p className="text-sm font-semibold text-ink truncate">{vendor.business_name}</p>
 <p className="text-xs text-gray-500 truncate">{vendor.location || '—'}</p>
 </div>
 </div>
 <div className="flex items-center gap-4 text-sm shrink-0">
 <span className="font-bold text-ink tabular-nums">
 {getNumeric(vendor.performance_score).toFixed(1)}
 </span>
 <span className="text-gray-500 tabular-nums">
 {getNumeric(vendor.completion_rate).toFixed(0)}% complete
 </span>
 <span className="text-gray-500 tabular-nums">
 ★ {getNumeric(vendor.average_rating).toFixed(1)}
 </span>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}
 </>
 )}
 </div>
 );
};

export default AdminPerformance;
